import { useState, Fragment, memo } from 'react'

import isEqual from 'react-fast-compare'
import {
  AreaChart as RechartsAreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import { AnalyticsResponse } from '@tribeplatform/gql-client/types'

import type { GroupBy, ChartTimeFrame } from '../types/index.js'
import {
  DefaultChartColors,
  AREA_INACTIVE_OPACITY,
  CHART_ANIMATION_ACTIVE,
  CHART_ANIMATION_DURATION,
} from '../utils/constants.js'
import {
  analyticsNumberFormatter,
  rechartsLineChartParser as rechartsAreaChartParser,
} from '../utils/parser.js'

import { CustomLegend } from './CustomLegend.js'
import { CustomToolTipRechartsHOC } from './CustomToolTipRecharts.js'

interface Props {
  data: {
    chartData: AnalyticsResponse
    timeFrame: ChartTimeFrame
    indexBy: string[]
    labels: string[]
    groupBy?: GroupBy[]
    ColorTheme?: string[]
    syncId?: string
    id?: string
  }
}

export const AreaChartRecharts = memo(
  ({
    data: {
      chartData,
      timeFrame,
      indexBy,
      labels,
      groupBy,
      ColorTheme = DefaultChartColors,
      syncId,
      id,
    },
  }: Props) => {
    const [activeIndex, setActiveIndex] = useState(-1)

    const getOpacity = (index: number) => {
      if (activeIndex === -1) {
        return 1
      }

      if (index === activeIndex) {
        return 1
      }

      return AREA_INACTIVE_OPACITY
    }

    return (
      <>
        <CustomLegend
          labels={labels}
          colors={ColorTheme}
          className="mt-2"
          setActiveIndex={setActiveIndex}
        />
        <ResponsiveContainer width="99%" height={320}>
          <RechartsAreaChart
            data={rechartsAreaChartParser(
              chartData,
              indexBy,
              timeFrame,
              groupBy,
            )}
            syncId={syncId}
            margin={{ top: 20, left: 0, right: 0, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="0"
              vertical={false}
              stroke="#EAF0F4"
            />
            <XAxis
              tick={{ fontSize: 12 }}
              tickMargin={12}
              dataKey="date"
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={0}
              width={30}
              // fontSize is being overridden by tribe's global selectors
              // so we have to use style here to set the fontsize
              // tick={{ fontSize: 12 }}
              style={{ fontSize: 12 }}
              tickFormatter={value => analyticsNumberFormatter(value)}
            />
            <Tooltip
              content={CustomToolTipRechartsHOC({ labels })}
              cursor={{
                stroke: '#C6C6D2',
                strokeDasharray: '5 5',
                fill: 'transparent',
              }}
            />
            {indexBy.map((label, index) => (
              <Fragment key={label}>
                <defs>
                  <linearGradient
                    id={`areaGradient${label.replace(/\s/g, '')}${index}${
                      id || ''
                    }`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={ColorTheme[index]}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={ColorTheme[index]}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <Area
                  type="linear"
                  dataKey={label}
                  stroke={ColorTheme[index]}
                  fill={`url(#areaGradient${label.replace(/\s/g, '')}${index}${
                    id || ''
                  })`}
                  strokeWidth={2}
                  fillOpacity={0.5}
                  isAnimationActive={CHART_ANIMATION_ACTIVE}
                  animationDuration={CHART_ANIMATION_DURATION}
                  opacity={getOpacity(index)}
                />
              </Fragment>
            ))}
          </RechartsAreaChart>
        </ResponsiveContainer>
      </>
    )
  },
  isEqual,
)
